import React, { ReactNode } from 'react'
import * as datefns from 'date-fns'
import i18n from 'i18next'
import { getIntlDateLocale, getIntlLocale } from '../../i18n/localeUtils'
import { resolveLanguage } from '../../i18n/languagePreference'

const nullOrUndefined = (value: string | number | null | undefined) => value === null || value === undefined

type formatNumberOptions = {
    locale?: string
    decimals?: number
    minDecimals?: number
    maxDecimals?: number
    suffix?: string
    prefix?: string
    divisor?: number
    signDisplay?: Intl.NumberFormatOptions['signDisplay']
}

/**
 * Format a number.
 * @param {Number|string|null} number the number to format
 * @param {object} options These are the options:
 * - locale: the locale to use, default 'en-US'
 * - decimals: the number of decimals to show, default 2
 * - minDecimals: the minimum number of decimals to show, default 2
 * - maxDecimals: the maximum number of decimals to show, default 2
 * - suffix: a suffix to add to the number, default ''
 * - prefix: a prefix to add to the number, default ''
 * - divisor: a number to divide the number by before formatting, default 1
 *
 * @returns {string} the formatted number
 */
function normalizeIntlLocale(locale?: string) {
    if (!locale) {
        return getIntlLocale(i18n.language)
    }

    if (locale.startsWith('sv')) {
        return 'sv-SE'
    }

    if (locale.startsWith('en')) {
        return 'en-US'
    }

    return locale
}

function getBigMoneyScaleSuffixes(locale?: string) {
    if (resolveLanguage(locale ?? i18n.language) === 'en') {
        return { thousand: 'k', million: 'M', billion: 'bn' }
    }

    return { thousand: 'k', million: 'M', billion: 'MD' }
}

function formatNumber(number?: number | string | null, options: formatNumberOptions = {}) {
    if (number === undefined || number === null || !isFinite(+number) || number === '') return ''

    const locale = normalizeIntlLocale(options.locale)
    const decimals = options.decimals ?? 2
    const suffix = options.suffix ?? ''
    const prefix = options.prefix ?? ''
    const divisor = options.divisor ?? 1
    const minDecimals = options.minDecimals ?? decimals
    const maxDecimals = options.maxDecimals ?? decimals

    number = +number / divisor
    number = new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals,
        signDisplay: options.signDisplay,
    }).format(number)
    return `${prefix}${number}${suffix}`
}

type formatMoneyOptions = formatNumberOptions & {
    currencySuffix?: string
    currencySymbol?: string | null
    suffix?: string
    prefix?: string
}

/**
 * Formats a number as money.
 *
 * @param {Number|String|Null} number the number to format
 * @param {object} options same as formatNumber, plus: 'currencySymbol' and 'currencySuffix' (default true)
 * @returns {string} the formatted number
 */
function formatMoney(number?: number | string | null, options: formatMoneyOptions = {}) {
    const extraOptions: formatNumberOptions = {}
    const currencySuffix = nullOrUndefined(options.currencySuffix) ? true : options.currencySuffix
    if (options.currencySymbol) {
        if (currencySuffix) {
            const suffix = options.suffix ?? ''
            extraOptions.suffix = `${'\u00A0'}${suffix}${options.currencySymbol}`
        } else {
            const prefix = options.prefix ?? ''
            extraOptions.prefix = `${prefix}${options.currencySymbol}${'\u00A0'}`
        }
    }

    if (
        nullOrUndefined(options.decimals) &&
        nullOrUndefined(options.maxDecimals) &&
        nullOrUndefined(options.minDecimals) &&
        !nullOrUndefined(number)
    ) {
        const absNumber = Math.abs(+number)

        if (absNumber >= 1) {
            options.minDecimals = 0
            options.maxDecimals = 3
        } else {
            options.minDecimals = 2
            options.maxDecimals = 5
        }
    }

    return formatNumber(number, {
        ...options,
        ...extraOptions,
    })
}

function formatPercentage(number?: number | string | null, options: formatNumberOptions = {}) {
    return formatNumber(number, {
        ...options,
        suffix: '%',
    })
}

export const formatBigNumber = (
    number: number,
    options: Omit<Parameters<typeof formatBigMoney>[1], 'currencySymbol'>,
) => {
    return formatBigMoney(number, options)
}

function formatBigMoney(
    number: number,
    options: { currencySymbol?: string | null; decimals?: number; locale?: string; tooltip: true },
): ReactNode
function formatBigMoney(
    number: number,
    options: { currencySymbol?: string | null; decimals?: number; locale?: string; tooltip?: false },
): string
function formatBigMoney(
    number: number,
    options: { currencySymbol?: string | null; decimals?: number; locale?: string; tooltip?: boolean } = {},
): string | ReactNode {
    let decimals = options.decimals ?? 0
    const extraOptions: formatMoneyOptions = {}
    const scaleSuffixes = getBigMoneyScaleSuffixes(options.locale)

    if (Math.abs(number) >= 100_000_000_000) {
        // When absolute value >= 100,000,000,000
        extraOptions.suffix = scaleSuffixes.billion
        extraOptions.divisor = 1_000_000_000
        decimals = 0
    } else if (Math.abs(number) >= 10_000_000_000) {
        // When absolute value >= 10,000,000,000
        extraOptions.suffix = scaleSuffixes.billion
        extraOptions.divisor = 1_000_000_000
        decimals = 1
    } else if (Math.round(Math.abs(number) / 100_000) >= 100) {
        // When absolute value rounded to the nearest 100k is >= 10,000,000
        extraOptions.suffix = scaleSuffixes.million
        extraOptions.divisor = 1_000_000
        decimals = 0
    } else if (Math.round(Math.abs(number) / 1_000) >= 1000) {
        // When absolute value rounded to the nearest 1k is >= 1,000,000
        extraOptions.suffix = scaleSuffixes.million
        extraOptions.divisor = 1_000_000
        decimals = 1
    } else if (Math.abs(number) >= 1_000) {
        // When absolute value >= 1,000
        extraOptions.suffix = scaleSuffixes.thousand
        extraOptions.divisor = 1_000
        decimals = 0
    } else {
        extraOptions.divisor = 1
    }

    const result = formatMoney(number, {
        ...options,
        ...extraOptions,
        decimals,
    })

    if (options.tooltip) {
        return <span title={`${formatMoney(number, options)}`}>{result}</span>
    }

    return result
}
const yyyymmddDateFormatOptions: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    year: 'numeric',
    day: '2-digit',
}

const yyyymmddFormatters = {
    'sv-SE': new Intl.DateTimeFormat('sv-SE', yyyymmddDateFormatOptions),
    'en-US': new Intl.DateTimeFormat('en-US', yyyymmddDateFormatOptions),
} as const

const mmddDateFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
}

const mmddFormatters = {
    'sv-SE': new Intl.DateTimeFormat('sv-SE', mmddDateFormatOptions),
    'en-US': new Intl.DateTimeFormat('en-US', mmddDateFormatOptions),
} as const

const yyyymmddhhmmDateFormatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
}

const yyyymmddhhmmssDateFormatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
}

const yyyymmddhhmmFormatters = {
    'sv-SE': new Intl.DateTimeFormat('sv-SE', yyyymmddhhmmDateFormatOptions),
    'en-US': new Intl.DateTimeFormat('en-US', yyyymmddhhmmDateFormatOptions),
} as const

const yyyymmddhhmmssFormatters = {
    'sv-SE': new Intl.DateTimeFormat('sv-SE', yyyymmddhhmmssDateFormatOptions),
    'en-US': new Intl.DateTimeFormat('en-US', yyyymmddhhmmssDateFormatOptions),
} as const

function getFormatterYYYYMMDD(locale?: string) {
    const intlLocale = getIntlDateLocale(locale ?? i18n.language)

    return yyyymmddFormatters[intlLocale]
}

function getFormatterMMDD(locale?: string) {
    const intlLocale = getIntlDateLocale(locale ?? i18n.language)

    return mmddFormatters[intlLocale]
}

function getFormatterYYYYMMDDhhmm(locale?: string) {
    const intlLocale = getIntlDateLocale(locale ?? i18n.language)

    return yyyymmddhhmmFormatters[intlLocale]
}

function getFormatterYYYYMMDDhhmmss(locale?: string) {
    const intlLocale = getIntlDateLocale(locale ?? i18n.language)

    return yyyymmddhhmmssFormatters[intlLocale]
}

function applyNonBreakingDateSeparators(formattedDate: string) {
    return formattedDate.replace(/-/g, '\u2011')
}

type FormatDateWithLocaleOptions = {
    locale?: string
    disableNonBreakingDash?: boolean
}

type FormatDateYYYYMMDDOptions = FormatDateWithLocaleOptions

function formatDateYYYYMMDD(date?: string | Date | null, options?: boolean | FormatDateYYYYMMDDOptions) {
    if (!date) return ''
    if (typeof date === 'string') {
        date = new Date(date)
    }

    const { locale, disableNonBreakingDash = false } =
        typeof options === 'boolean' ? { disableNonBreakingDash: options } : (options ?? {})

    const yyyymmdd = disableNonBreakingDash
        ? datefns.format(date, 'yyyy-MM-dd')
        : getFormatterYYYYMMDD(locale).format(date)

    if (disableNonBreakingDash) return yyyymmdd

    return applyNonBreakingDateSeparators(yyyymmdd)
}

type FormatDateMMDDOptions = {
    locale?: string
}

function formatDateMMDD(date?: string | Date | null, options?: FormatDateMMDDOptions) {
    if (!date) return ''
    if (typeof date === 'string') {
        date = new Date(date)
    }

    const { locale } = options ?? {}

    return applyNonBreakingDateSeparators(getFormatterMMDD(locale).format(date))
}

function formatDateYYYYMMDDhhmm(date?: string | Date | null, options?: FormatDateWithLocaleOptions) {
    if (!date) return ''
    if (typeof date === 'string') {
        date = new Date(date)
    }

    const { locale, disableNonBreakingDash = false } = options ?? {}

    const formatted = disableNonBreakingDash
        ? datefns.format(date, 'yyyy-MM-dd HH:mm')
        : getFormatterYYYYMMDDhhmm(locale).format(date)

    if (disableNonBreakingDash) return formatted

    return applyNonBreakingDateSeparators(formatted)
}

function formatDateYYYYMMDDhhmmss(date?: string | Date | null, options?: FormatDateWithLocaleOptions) {
    if (!date) return ''
    if (typeof date === 'string') {
        date = new Date(date)
    }

    const { locale, disableNonBreakingDash = false } = options ?? {}

    const formatted = disableNonBreakingDash
        ? datefns.format(date, 'yyyy-MM-dd HH:mm:ss')
        : getFormatterYYYYMMDDhhmmss(locale).format(date)

    if (disableNonBreakingDash) return formatted

    return applyNonBreakingDateSeparators(formatted)
}

function formatInteger(number: string | number | null, props: formatNumberOptions = {}) {
    return formatNumber(number, { decimals: 0, ...props })
}

function roundFloat(number: number, decimals: number = 2): number {
    if (isNaN(number)) return 0

    return Math.round((number + Number.EPSILON) * 10 ** decimals) / 10 ** decimals
}

export {
    formatNumber,
    formatInteger,
    formatMoney,
    formatPercentage,
    formatBigMoney,
    formatDateMMDD,
    formatDateYYYYMMDD,
    formatDateYYYYMMDDhhmm,
    formatDateYYYYMMDDhhmmss,
    roundFloat,
}
